/**
 * P3-27/28: Trade tab 按钮模型 hooks。
 *
 * - TradeButton：TradeButtonController，快照含 tradeMax/half/fifth 与显示条件。
 * - EmbassyButton：EmbassyButtonController，自定义 EmbassyButtonHelper tooltip。
 * - autoPinnedButton / SendExplorers / feed/bcoin/crash：复用 ButtonModernController
 *   族或专用 controller。
 */
import { useCallback, useMemo } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { t } from "../../bridge/i18n";
import {
  useButtonModel,
  type ModernButtonController,
} from "../bonfire/useButtonModel";
import { useScienceButton } from "../science/useScienceModel";

// ---------------------------------------------------------------------------
// 通用类型
// ---------------------------------------------------------------------------

export interface RaceLike {
  name: string;
  title: string;
  standing: number;
  embassyLevel: number;
  embassyPrices: { name: string; val: number }[];
  pinned: boolean;
  autoPinned?: boolean;
  unlocked: boolean;
  collapsed: boolean;
  buys: { name: string; val: number }[];
  sells: {
    name: string;
    value: number;
    chance: number;
    width: number;
    minLevel?: number;
    seasons?: Record<string, number>;
  }[];
  energy?: number;
  duration?: number;
}

export interface TradeButtonSnapshot {
  name: string;
  description: string;
  visible: boolean;
  enabled: boolean;
  resourceIsLimited: string | boolean;
  tradeMax: number;
  tradeHalf: number;
  tradeFifth: number;
  showHalf: boolean;
  showFifth: boolean;
  usePercentage: boolean;
}

export interface EmbassyButtonSnapshot {
  name: string;
  description: string;
  visible: boolean;
  enabled: boolean;
  resourceIsLimited: string | boolean;
  pinned: boolean;
  hasEmbassy: boolean;
}

export interface ModernActionSnapshot {
  name: string;
  description: string;
  visible: boolean;
  enabled: boolean;
  resourceIsLimited: string | boolean;
  pinned?: boolean;
}

// ---------------------------------------------------------------------------
// TradeButton
// ---------------------------------------------------------------------------

type ControllerCtor = new (game: GamePage) => ModernButtonController;

function getController(
  getter: () => ControllerCtor | undefined,
): ModernButtonController | null {
  const game = getGame();
  const Ctor = getter();
  if (!Ctor || !game) {
    return null;
  }
  return new Ctor(game);
}

function getTradeControllerCtor(): ControllerCtor | undefined {
  const w = window as unknown as {
    com?: {
      nuclearunicorn?: {
        game?: {
          ui?: { TradeButtonController?: ControllerCtor };
        };
      };
    };
  };
  return w.com?.nuclearunicorn?.game?.ui?.TradeButtonController;
}

function toTradeSnapshot(model: Record<string, unknown>): TradeButtonSnapshot {
  const race = (model.options as { race?: RaceLike } | undefined)?.race;
  const game = getGame();
  const dip = game?.diplomacy as
    | {
        getMaxTradeAmt: (race: RaceLike) => number;
        hasMultipleResources: (race: RaceLike, pct: number) => boolean;
      }
    | undefined;
  const opts = game?.opts as
    | {
        usePercentageConsumptionValues?: boolean;
        showNonApplicableButtons?: boolean;
      }
    | undefined;

  const tradeMax = race && dip ? dip.getMaxTradeAmt(race) : 0;
  const tradeHalf = Math.floor(tradeMax / 2);
  const tradeFifth = Math.floor(tradeMax / 5);
  const usePercentage = !!opts?.usePercentageConsumptionValues;
  const showHalf = !!opts?.showNonApplicableButtons || (race ? dip?.hasMultipleResources(race, 50) : false);
  const showFifth = !!opts?.showNonApplicableButtons || (race ? dip?.hasMultipleResources(race, 25) : false);

  return {
    name: String(model.name ?? ""),
    description: String(model.description ?? ""),
    visible: !!model.visible,
    enabled: !!model.enabled,
    resourceIsLimited: (model.resourceIsLimited as string | boolean) ?? "",
    tradeMax,
    tradeHalf,
    tradeFifth,
    showHalf: !!showHalf,
    showFifth: !!showFifth,
    usePercentage,
  };
}

function tradeSnapshotEq(a: TradeButtonSnapshot | null, b: TradeButtonSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return (
    a.name === b.name &&
    a.description === b.description &&
    a.visible === b.visible &&
    a.enabled === b.enabled &&
    a.resourceIsLimited === b.resourceIsLimited &&
    a.tradeMax === b.tradeMax &&
    a.tradeHalf === b.tradeHalf &&
    a.tradeFifth === b.tradeFifth &&
    a.showHalf === b.showHalf &&
    a.showFifth === b.showFifth &&
    a.usePercentage === b.usePercentage
  );
}

export interface TradeButtonModelResult {
  snap: TradeButtonSnapshot | null;
  modelRef: React.MutableRefObject<Record<string, unknown> | null>;
  controller: ModernButtonController | null;
  getTooltip: () => string;
}

export function useTradeButtonModel(raceName: string): TradeButtonModelResult {
  const controller = useMemo(() => getController(getTradeControllerCtor), []);

  const createOpts = useCallback(
    (game: GamePage) => {
      const dip = game.diplomacy as {
        get: (name: string) => RaceLike;
        getManpowerCost: () => number;
        getGoldCost: () => number;
      };
      const race = dip.get(raceName);
      const prices = [
        { name: "manpower", val: dip.getManpowerCost() },
        { name: "gold", val: dip.getGoldCost() },
        ...race.buys,
      ];
      return {
        name: t("trade.send.caravan"),
        description: t("trade.send.caravan.desc"),
        prices,
        race,
      };
    },
    [raceName],
  );

  const { snap, modelRef } = useButtonModel(
    controller,
    createOpts,
    toTradeSnapshot,
    tradeSnapshotEq,
  );

  const getTooltip = useCallback(() => {
    const g = getGame();
    const helper = window.ButtonModernHelper;
    if (!g || !controller || !helper) {
      return "";
    }
    return helper.getTooltipHTML(controller, { options: createOpts(g) });
  }, [controller, createOpts]);

  return { snap, modelRef, controller, getTooltip };
}

// ---------------------------------------------------------------------------
// EmbassyButton
// ---------------------------------------------------------------------------

function getEmbassyControllerCtor(): ControllerCtor | undefined {
  const w = window as unknown as {
    classes?: {
      diplomacy?: {
        ui?: { EmbassyButtonController?: ControllerCtor };
      };
    };
  };
  return w.classes?.diplomacy?.ui?.EmbassyButtonController;
}

function toEmbassySnapshot(model: Record<string, unknown>): EmbassyButtonSnapshot {
  const race = (model.options as { race?: RaceLike } | undefined)?.race;
  return {
    name: String(model.name ?? ""),
    description: String(model.description ?? ""),
    visible: !!model.visible,
    enabled: !!model.enabled,
    resourceIsLimited: (model.resourceIsLimited as string | boolean) ?? "",
    pinned: !!race?.pinned,
    hasEmbassy: (race?.embassyLevel ?? 0) > 0,
  };
}

function embassySnapshotEq(
  a: EmbassyButtonSnapshot | null,
  b: EmbassyButtonSnapshot | null,
): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return (
    a.name === b.name &&
    a.description === b.description &&
    a.visible === b.visible &&
    a.enabled === b.enabled &&
    a.resourceIsLimited === b.resourceIsLimited &&
    a.pinned === b.pinned &&
    a.hasEmbassy === b.hasEmbassy
  );
}

export interface EmbassyButtonModelResult {
  snap: EmbassyButtonSnapshot | null;
  modelRef: React.MutableRefObject<Record<string, unknown> | null>;
  controller: ModernButtonController | null;
  getTooltip: () => string;
}

export function useEmbassyButtonModel(raceName: string): EmbassyButtonModelResult {
  const controller = useMemo(() => getController(getEmbassyControllerCtor), []);

  const createOpts = useCallback(
    (game: GamePage) => {
      const dip = game.diplomacy as { get: (name: string) => RaceLike };
      const race = dip.get(raceName);
      return {
        prices: race.embassyPrices,
        race,
      };
    },
    [raceName],
  );

  const { snap, modelRef } = useButtonModel(
    controller,
    createOpts,
    toEmbassySnapshot,
    embassySnapshotEq,
  );

  const getTooltip = useCallback(() => {
    const g = getGame();
    const helper = (window as unknown as { EmbassyButtonHelper?: { getTooltipHTML: (c: ModernButtonController, m: { options: Record<string, unknown> }) => string } }).EmbassyButtonHelper;
    if (!g || !controller || !helper) {
      return "";
    }
    return helper.getTooltipHTML(controller, { options: createOpts(g) });
  }, [controller, createOpts]);

  return { snap, modelRef, controller, getTooltip };
}

// ---------------------------------------------------------------------------
// autoPinnedButton
// ---------------------------------------------------------------------------

function getAutoPinnedControllerCtor(): ControllerCtor | undefined {
  const w = window as unknown as {
    classes?: {
      diplomacy?: {
        ui?: { autoPinnedButtonController?: ControllerCtor };
      };
    };
  };
  return w.classes?.diplomacy?.ui?.autoPinnedButtonController;
}

export interface AutoPinnedButtonModelResult {
  snap: ModernActionSnapshot | null;
  modelRef: React.MutableRefObject<Record<string, unknown> | null>;
  controller: ModernButtonController | null;
  getTooltip: () => string;
}

export function useAutoPinnedButtonModel(
  raceName: string,
): AutoPinnedButtonModelResult {
  const controller = useMemo(
    () => getController(getAutoPinnedControllerCtor),
    [],
  );

  const createOpts = useCallback(
    (game: GamePage) => {
      const dip = game.diplomacy as { get: (name: string) => RaceLike };
      const race = dip.get(raceName);
      return {
        name: race.autoPinned
          ? t("trade.autopinned.labelOn")
          : t("trade.autopinned.labelOff"),
        description: t("trade.autopinned.desc"),
        race,
        handler: () => {
          race.autoPinned = !race.autoPinned;
          game.render?.();
        },
      };
    },
    [raceName],
  );

  const toSnapshot = useCallback(
    (model: Record<string, unknown>): ModernActionSnapshot => {
      const race = (model.options as { race?: RaceLike } | undefined)?.race;
      return {
        name: String(model.name ?? ""),
        description: String(model.description ?? ""),
        visible: !!model.visible,
        enabled: !!model.enabled,
        resourceIsLimited: (model.resourceIsLimited as string | boolean) ?? "",
        pinned: !!race?.pinned,
      };
    },
    [],
  );

  const snapshotEq = useCallback(
    (a: ModernActionSnapshot | null, b: ModernActionSnapshot | null): boolean => {
      if (a === b) {
        return true;
      }
      if (!a || !b) {
        return false;
      }
      return (
        a.name === b.name &&
        a.description === b.description &&
        a.visible === b.visible &&
        a.enabled === b.enabled &&
        a.resourceIsLimited === b.resourceIsLimited &&
        a.pinned === b.pinned
      );
    },
    [],
  );

  const { snap, modelRef } = useButtonModel(
    controller,
    createOpts,
    toSnapshot,
    snapshotEq,
  );

  const getTooltip = useCallback(() => {
    const g = getGame();
    const helper = window.ButtonModernHelper;
    if (!g || !controller || !helper) {
      return "";
    }
    return helper.getTooltipHTML(controller, { options: createOpts(g) });
  }, [controller, createOpts]);

  return { snap, modelRef, controller, getTooltip };
}

// ---------------------------------------------------------------------------
// Send Explorers
// ---------------------------------------------------------------------------

function getSendExplorersControllerCtor(): ControllerCtor | undefined {
  const w = window as unknown as {
    classes?: {
      trade?: {
        ui?: { SendExplorersButtonController?: ControllerCtor };
      };
    };
  };
  return w.classes?.trade?.ui?.SendExplorersButtonController;
}

export function useSendExplorersButtonModel(): {
  snap: ModernActionSnapshot | null;
  modelRef: React.MutableRefObject<Record<string, unknown> | null>;
  controller: ModernButtonController | null;
  getTooltip: () => string;
} {
  const controller = useMemo(
    () => getController(getSendExplorersControllerCtor),
    [],
  );

  const createOpts = useCallback(
    () => ({
      name: t("trade.send.explorers"),
      description: t("trade.send.explorers.desc"),
      prices: [{ name: "manpower", val: 1000 }],
    }),
    [],
  );

  const toSnapshot = useCallback(
    (model: Record<string, unknown>): ModernActionSnapshot => ({
      name: String(model.name ?? ""),
      description: String(model.description ?? ""),
      visible: !!model.visible,
      enabled: !!model.enabled,
      resourceIsLimited: (model.resourceIsLimited as string | boolean) ?? "",
    }),
    [],
  );

  const snapshotEq = useCallback(
    (a: ModernActionSnapshot | null, b: ModernActionSnapshot | null): boolean => {
      if (a === b) {
        return true;
      }
      if (!a || !b) {
        return false;
      }
      return (
        a.name === b.name &&
        a.description === b.description &&
        a.visible === b.visible &&
        a.enabled === b.enabled &&
        a.resourceIsLimited === b.resourceIsLimited
      );
    },
    [],
  );

  const { snap, modelRef } = useButtonModel(
    controller,
    createOpts,
    toSnapshot,
    snapshotEq,
  );

  const getTooltip = useCallback(() => {
    const g = getGame();
    const helper = window.ButtonModernHelper;
    if (!g || !controller || !helper) {
      return "";
    }
    return helper.getTooltipHTML(controller, { options: createOpts() });
  }, [controller, createOpts]);

  return { snap, modelRef, controller, getTooltip };
}

// ---------------------------------------------------------------------------
// 通用 Modern 动作按钮（feed elders / buyBcoin / sellBcoin / crashBcoin）
// ---------------------------------------------------------------------------

function getButtonModernControllerCtor(): ControllerCtor | undefined {
  const w = window as unknown as {
    com?: {
      nuclearunicorn?: {
        game?: {
          ui?: { ButtonModernController?: ControllerCtor };
        };
      };
    };
  };
  return w.com?.nuclearunicorn?.game?.ui?.ButtonModernController;
}

function getCrashBcoinControllerCtor(): ControllerCtor | undefined {
  const w = window as unknown as {
    com?: {
      nuclearunicorn?: {
        game?: {
          ui?: { CrashBcoinButtonController?: ControllerCtor };
        };
      };
    };
  };
  return w.com?.nuclearunicorn?.game?.ui?.CrashBcoinButtonController;
}

export function useModernActionButtonModel(
  id: string,
  name: string,
  description: string,
  handler: (...args: unknown[]) => void,
  getControllerCtor: () => ControllerCtor | undefined = getButtonModernControllerCtor,
): {
  snap: ModernActionSnapshot | null;
  modelRef: React.MutableRefObject<Record<string, unknown> | null>;
  controller: ModernButtonController | null;
  getTooltip: () => string;
} {
  return useScienceButton(id, getControllerCtor, {
    name,
    description,
    handler,
  });
}

export function useCrashBcoinButtonModel(
  handler: (...args: unknown[]) => void,
): {
  snap: ModernActionSnapshot | null;
  modelRef: React.MutableRefObject<Record<string, unknown> | null>;
  controller: ModernButtonController | null;
  getTooltip: () => string;
} {
  return useModernActionButtonModel(
    "crashBcoin",
    t("trade.bcoin.crash"),
    t("trade.bcoin.crash.desc"),
    handler,
    getCrashBcoinControllerCtor,
  );
}
